<!-- sm-file-uploader.vue (patched) -->
<template>
    <el-form-item :label="label" :prop="prop" label-position="top">
        <el-upload
            class="w-100"
            drag
            :multiple="multiple"
            :file-list="fileList"
            :before-upload="beforeUpload"
            :on-change="handleFileChange"
            :on-remove="handleRemove"
            :on-error="handleError"
            :auto-upload="false"
            :show-file-list="true"
        >
            <div class="d-flex justify-center">
                <div>
                    <i style="font-size: 40pt" class="ti ti-cloud-up text-primary"></i>
                    <div class="el-upload__text">
                        {{ $t('Drop file here or') }} <em>{{ $t('click to upload') }}</em> {{ $t('file') }}
                        <div v-if="existingFile" class="text-muted mt-2">Current file: {{ existingFile.name }}</div>
                    </div>
                </div>
            </div>

            <template #tip>
                <div class="el-upload__tip d-none">Accepted: {{ accept.join(', ') }} (max 10MB each)</div>
            </template>
        </el-upload>
    </el-form-item>
</template>

<script setup lang="ts">
    import { ref, computed, watch } from 'vue'
    import axios from '@/utils/apiClient'
    import { ElMessage } from 'element-plus'
    import { useAuthStore } from '@/stores/auth'
    import type { UploadFile, UploadUserFile } from 'element-plus'

    const emit = defineEmits(['update:modelValue'])

    const props = defineProps({
        modelValue: { type: [Array, String] as () => string[] | string, default: () => [] },
        label: { type: String, default: 'Upload File' },
        prop: { type: String, default: null },
        folder: { type: String, default: 'uploads' },
        isPrivate: { type: Boolean, default: false },
        api: { type: String, default: `${import.meta.env.VITE_API_BASE_URL}api/Upload` },
        meta: { type: Object as () => Record<string, any>, default: () => ({}) },
        accept: {
            type: Array as () => string[],
            default: () => [
                'image/',
                'application/pdf',
                'text/plain',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/zip',
                'application/x-zip-compressed',
                'application/x-rar-compressed',
            ],
        },
        multiple: { type: Boolean, default: true },
        isEditMode: { type: Boolean, default: false },
    })

    const authStore = useAuthStore()

    // UI lists
    const fileList = ref<UploadUserFile[]>([])
    const existingFile = ref<UploadUserFile | null>(null)

    // Keep selected (new) files as UploadUserFile[] so we have `uid`
    const selectedUploadFiles = ref<UploadUserFile[]>([])

    // For edit flow (if you need server-side delete later)
    const filesToRemove = ref<UploadUserFile[]>([])

    const modelValueArray = computed(() =>
        Array.isArray(props.modelValue) ? props.modelValue : props.modelValue ? [props.modelValue] : [],
    )

    const getFilenameFromPath = (path: string): string => path.split(/(\\|\/)/g).pop() || path

    const beforeUpload = (file: File) => {
        const isValid = props.accept.some(type => file.type.startsWith(type))
        if (!isValid) {
            ElMessage.error('Invalid file type.')
            return false
        }
        if (file.size > 10 * 1024 * 1024) {
            ElMessage.error('File must be smaller than 10MB.')
            return false
        }
        return true
    }

    const updateFileList = () => {
        fileList.value = [
            ...(existingFile.value ? [{ ...existingFile.value, status: 'success' as const }] : []),
            ...selectedUploadFiles.value.map(f => ({
                name: f.name,
                status: f.status || 'ready',
                percentage: f.percentage || 0,
                raw: f.raw!,
                uid: f.uid,
            })),
        ]
    }

    const handleFileChange = (_uploadFile: UploadFile, uploadFiles: UploadUserFile[]) => {
        // Only keep files that are not already selected and not marked for removal
        const fresh = uploadFiles
            .filter(f => f.raw && !filesToRemove.value.some(r => r.uid === f.uid))
            .filter(f => !selectedUploadFiles.value.some(s => s.uid === f.uid))

        selectedUploadFiles.value = [...selectedUploadFiles.value, ...fresh]
        updateFileList()
    }

    const handleRemove = (file: UploadFile) => {
        // Remove from selected new files by uid
        if (file.uid) {
            selectedUploadFiles.value = selectedUploadFiles.value.filter(f => f.uid !== file.uid)
        }

        // Remove "existing" file (edit) UI
        if (existingFile.value?.uid === file.uid || existingFile.value?.url === file.url) {
            filesToRemove.value.push(file as UploadUserFile)
            existingFile.value = null
            emit('update:modelValue', props.multiple ? [] : '')
        }
        updateFileList()
    }

    const handleError = (err: Error) => {
        ElMessage.error('Upload error: ' + err.message)
    }

    // Expose helpers for the parent (THIS is the key fix)
    const getFiles = (): File[] => selectedUploadFiles.value.map(f => f.raw!).filter(Boolean) as File[]

    const appendTo = (fd: FormData, fieldName = 'files') => {
        getFiles().forEach(file => fd.append(fieldName, file))
    }

    const clear = () => {
        selectedUploadFiles.value = []
        filesToRemove.value = []
        updateFileList()
    }

    // Keep your optional direct-uploader method for other pages
    const uploadFiles = async (meta: Record<string, any> = {}): Promise<string[]> => {
        if (selectedUploadFiles.value.length === 0 && !props.isEditMode) return []

        const formData = new FormData()
        selectedUploadFiles.value.forEach(f => formData.append('files', f.raw!))
        Object.entries({ ...props.meta, ...meta }).forEach(([k, v]) => v != null && formData.append(k, String(v)))
        formData.append('folder', props.folder)
        formData.append('private', props.isPrivate.toString())

        const response = await axios.post(props.api, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${authStore.user.token}`,
            },
        })

        let paths: string[] = []
        if (Array.isArray(response.data))
            paths = response.data.map((it: any) => (typeof it === 'string' ? it : it?.path)).filter(Boolean)
        else if (response.data?.path) paths = [response.data.path]

        const newValue = props.multiple ? paths : paths[0]
        emit('update:modelValue', newValue)
        clear()
        return paths
    }

    // Sync initial modelValue to show an existing file
    watch(
        modelValueArray,
        val => {
            if (val.length > 0) {
                existingFile.value = { name: getFilenameFromPath(val[0]), url: val[0] } as UploadUserFile
            } else {
                existingFile.value = null
            }
            updateFileList()
        },
        { immediate: true },
    )

    defineExpose({
        // 🔑 Parent can call these:
        getFiles, // => File[]
        appendTo, // => append files into FormData
        clear,
        uploadFiles, // kept for other usage
        hasNewFiles: computed(() => selectedUploadFiles.value.length > 0),
    })
</script>
